// Dev test-harness token storage.
//
// Tokens come from POST /api/dev/quickstart (backend routers/dev_quickstart.py) and
// grant a dev-only bypass through the Clerk auth deps on the teacher and
// admin paths. This module is just thin localStorage accessors — the auth
// decision happens server-side.
//
// Presence of a dev teacher token in localStorage is what makes the
// frontend "be" in dev mode: api/client.ts prefers it over the Clerk
// bearer on teacher paths, and the teacher dashboard synthesizes an
// organization stub so Clerk's <SignedIn> / useOrganization() don't block
// the page. See src/pages/DevQuickstart.tsx for the issuance entrypoint.

const DEV_TEACHER_TOKEN_KEY = 'devTeacherToken'
const DEV_TEACHER_DATA_KEY = 'devTeacherData'

export interface DevTeacherData {
  school_name: string
  teacher_name: string
  class_code: string
  // Surfaced in the DevQuickstart redesign's "Demo student" meta cell.
  // Optional so existing localStorage entries from before this field
  // was added continue to deserialize cleanly — the UI shows an em-dash
  // fallback when undefined.
  student_id?: string
}

export function getDevTeacherToken(): string | null {
  try {
    return localStorage.getItem(DEV_TEACHER_TOKEN_KEY)
  } catch {
    return null
  }
}

export function setDevTeacherToken(token: string | null) {
  if (token) localStorage.setItem(DEV_TEACHER_TOKEN_KEY, token)
  else localStorage.removeItem(DEV_TEACHER_TOKEN_KEY)
}

export function getDevTeacherData(): DevTeacherData | null {
  try {
    const raw = localStorage.getItem(DEV_TEACHER_DATA_KEY)
    if (!raw) return null
    return JSON.parse(raw) as DevTeacherData
  } catch {
    return null
  }
}

export function setDevTeacherData(data: DevTeacherData | null) {
  if (data) localStorage.setItem(DEV_TEACHER_DATA_KEY, JSON.stringify(data))
  else localStorage.removeItem(DEV_TEACHER_DATA_KEY)
}

/** True when a dev teacher token is stashed. Used to branch UI. */
export function isDevTeacherActive(): boolean {
  return !!getDevTeacherToken()
}

/**
 * Synthetic Clerk organization id used to stand in for a real Clerk org
 * while a dev-quickstart admin session is active. The admin dashboard's
 * data hooks (useAdminMe / useAdminDashboardSummary / useAdminMastery)
 * gate their fetch on a truthy ``organizationId`` and use it only as a
 * cache key — the actual rows come from the backend, scoped server-side
 * to the dev admin's school via the dev token. The value mirrors the
 * backend's ``_TEST_SCHOOL_CLERK_ORG_ID`` (routers/dev_quickstart.py)
 * for legibility only; nothing validates it against Clerk.
 */
export const DEV_ADMIN_ORG_ID = 'dev-quickstart-school'

/** Clear both the token and the associated metadata. */
export function clearDevTeacher() {
  setDevTeacherToken(null)
  setDevTeacherData(null)
}
